/**
 * Sleuteloverzicht — /sleutels (gap-analyse 26 sep, #6).
 *
 * De keytags van het pand, zoals de Airtable-interface: tabs 'Werkende tags' / 'Alle tags',
 * filters, eigenaar wijzigen (de vorige eigenaar schuift door naar 'Eerdere eigenaar') en
 * een nieuwe tag. Airtable blijft de bron; beheer (admin_settings).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
  Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { vraag } from '../Verbeterregister/verbeterApi';
import { LEEG_FILTER, filterTags, namen, ids, wijziging } from './sleutelLogica';
import { herstelVanAntwoord } from '../Verbeterregister/verbeterLogica';
import { useUserRole } from '../../contexts/UserRoleContext';

const api = {
  lijst: (vers = false) => vraag(`/Api/sleutels${vers ? '?vers=1' : ''}`),
  nieuw: (body) => vraag('/Api/sleutels', { method: 'POST', body }),
  wijzig: (id, body) => vraag(`/Api/sleutels/${id}`, { method: 'PATCH', body }),
};

const LEGE_TAG = { tag: '', status: 'Niet in gebruik', profiel: 'Standaard', toegang: '', huidigeEigenaar: [], opmerkingen: '' };

function TagDialoog({ open, tag, opties, personen, onClose, onOpgeslagen }) {
  const nieuw = !tag;
  const [f, setF] = useState(LEGE_TAG);
  const [bezig, setBezig] = useState(false);
  useEffect(() => { if (open) setF(tag ? { ...LEGE_TAG, ...tag, opmerkingen: tag.opmerkingen || '', toegang: tag.toegang || '', profiel: tag.profiel || '', status: tag.status || '' } : LEGE_TAG); }, [open, tag]);
  const zet = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const kiesbaar = useMemo(() => {
    const bekend = new Set(personen.map((p) => p.id));
    return [...personen, ...(f.huidigeEigenaar || []).filter((p) => !bekend.has(p.id))];
  }, [personen, f.huidigeEigenaar]);

  const wissel = !nieuw && JSON.stringify(ids(tag.huidigeEigenaar)) !== JSON.stringify(ids(f.huidigeEigenaar));
  const wegGaand = wissel ? (tag.huidigeEigenaar || []).filter((p) => !ids(f.huidigeEigenaar).includes(p.id)) : [];

  const opslaan = async () => {
    setBezig(true);
    try {
      if (nieuw) {
        if (!String(f.tag).trim()) { toast.error('Vul het tagnummer in'); return; }
        const body = { tag: Number(f.tag), status: f.status || null, profiel: f.profiel || null, toegang: f.toegang || null,
          huidigeEigenaar: ids(f.huidigeEigenaar), opmerkingen: f.opmerkingen || null };
        const { tag: gemaakt } = await api.nieuw(body);
        toast.success(`${gemaakt.naam} toegevoegd`);
        onOpgeslagen(gemaakt, null);
      } else {
        const body = wijziging(tag, f);
        if (!Object.keys(body).length) { onClose(); return; }
        const antwoord = await api.wijzig(tag.id, body);
        onOpgeslagen(antwoord.tag, herstelVanAntwoord(antwoord));
      }
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{nieuw ? 'Nieuwe tag' : tag.naam}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {nieuw && <TextField label="Tagnummer" type="number" value={f.tag} onChange={(e) => zet('tag')(e.target.value)} autoFocus />}
          <Autocomplete multiple options={kiesbaar} value={f.huidigeEigenaar || []} getOptionLabel={(o) => o.naam || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id} onChange={(_, v) => zet('huidigeEigenaar')(v)}
            renderInput={(p) => <TextField {...p} label="Huidige eigenaar" />} />
          {wegGaand.length > 0 && (
            <Typography sx={{ fontSize: 12.5, color: '#64748b', mt: -1 }}>{namen(wegGaand)} komt bij de eerdere eigenaren te staan.</Typography>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            {[['status', 'Status'], ['profiel', 'Profiel'], ['toegang', 'Toegang tot']].map(([k, label]) => (
              <TextField key={k} select size="small" label={label} value={f[k] || ''} onChange={(e) => zet(k)(e.target.value)}>
                <MenuItem value=""><em>—</em></MenuItem>
                {(opties?.[k] || []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </TextField>
            ))}
          </Box>
          <TextField label="Opmerkingen" value={f.opmerkingen} onChange={(e) => zet('opmerkingen')(e.target.value)} multiline minRows={2} />
          {!nieuw && tag.eerdereEigenaar?.length > 0 && (
            <Typography sx={{ fontSize: 12.5, color: '#94a3b8' }}>Eerdere eigenaren: {namen(tag.eerdereEigenaar)}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" disableElevation disabled={bezig} onClick={opslaan} sx={{ textTransform: 'none', fontWeight: 700 }}>{nieuw ? 'Toevoegen' : 'Opslaan'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Sleuteloverzicht() {
  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(true);
  const [tab, setTab] = useState('werkend');
  const [filter, setFilter] = useState(LEEG_FILTER);
  const [dialoog, setDialoog] = useState(null); // null | { tag: obj|null }

  const laad = useCallback(async (vers = false) => {
    setLaden(true);
    try { setData(await api.lijst(vers)); } catch (e) { toast.error(`Sleuteloverzicht laden mislukt: ${e.message}`); }
    finally { setLaden(false); }
  }, []);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  const tags = useMemo(() => data?.tags || [], [data]);
  const zichtbaar = useMemo(() => filterTags(tags, tab, filter), [tags, tab, filter]);
  const opties = data?.opties || {};
  const { permissions } = useUserRole() || {};
  // Bewerken alleen met schrijfrecht op Airtable (niet op test) én edit-recht op 'sleutels'.
  const schrijven = !!data?.schrijvenAan && !!permissions?.sleutels?.edit;
  const vervang = (t) => setData((d) => ({ ...d, tags: d.tags.some((x) => x.id === t.id)
    ? d.tags.map((x) => (x.id === t.id ? t : x)) : [...d.tags, t].sort((a, b) => (a.tag ?? 1e9) - (b.tag ?? 1e9)) }));

  const opgeslagen = (t, herstel) => {
    vervang(t);
    setDialoog(null);
    if (!herstel) return;
    // herstel = de échte vorige stand + 'verwacht'; is de tag sindsdien opnieuw gewijzigd,
    // dan weigert de server (409) en meldt de toast dat ongedaan maken niet meer kan.
    biedOngedaanAan(`${t.naam} bijgewerkt`, async () => {
      const { tag: terug } = await api.wijzig(t.id, herstel);
      vervang(terug);
    });
  };
  const zetF = (k) => (e) => setFilter((f) => ({ ...f, [k]: e.target.value }));
  const kop = { fontSize: 12, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' };

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader icon="vpn_key" title="Sleuteloverzicht" subtitle="Keytags van het pand: wie heeft welke tag, met welk profiel en welke toegang"
        accent={{ bg: '#e2e8f0', color: '#334155' }}
        rightActions={schrijven && (
          <Button variant="contained" disableElevation size="small" onClick={() => setDialoog({ tag: null })} sx={{ textTransform: 'none', fontWeight: 700 }}>＋ Nieuwe tag</Button>
        )} />
      {data && !data.schrijvenAan && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>Schrijven naar Airtable staat uit op test — je kunt hier kijken, niet wijzigen.</Alert>
      )}
      {data?.melding && <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>{data.melding}</Alert>}

      <Tabs value={tab} onChange={(_, v) => { setTab(v); setFilter((f) => ({ ...f, status: '' })); }} sx={{ mb: 1.5, minHeight: 36, '& .MuiTab-root': { textTransform: 'none', minHeight: 36, fontWeight: 600 } }}>
        <Tab value="werkend" label={`Werkende tags (${tags.filter((t) => t.werkend).length})`} />
        <Tab value="alle" label={`Alle tags (${tags.length})`} />
      </Tabs>

      <Paper variant="outlined" sx={{ p: 1.25, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField size="small" placeholder="Zoeken (tag, eigenaar, opmerking)" value={filter.zoek} onChange={zetF('zoek')} sx={{ minWidth: 240, flex: '0 1 300px' }} />
        {[['status', 'Status', tab === 'werkend' ? (data?.werkendeStatussen || []) : (opties.status || [])], ['profiel', 'Profiel', opties.profiel || []], ['toegang', 'Toegang tot', opties.toegang || []]].map(([k, label, lijst]) => (
          <TextField key={k} select size="small" label={label} value={filter[k]} onChange={zetF(k)} sx={{ width: 190 }}>
            <MenuItem value="">Alle</MenuItem>
            {lijst.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        ))}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
          {data?.stand ? `stand Airtable ${new Date(data.stand).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}` : ''}
        </Typography>
        <Button size="small" onClick={() => laad(true)} disabled={laden} sx={{ textTransform: 'none' }}>Vernieuwen</Button>
      </Paper>

      {laden && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Paper variant="outlined">
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Tag', 'Huidige eigenaar', 'Status', 'Profiel', 'Toegang tot', 'Eerdere eigenaar', 'Opmerkingen'].map((h) => <TableCell key={h} sx={kop}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {zichtbaar.map((t) => (
                  <TableRow key={t.id} hover onClick={schrijven ? () => setDialoog({ tag: t }) : undefined} sx={{ cursor: schrijven ? 'pointer' : 'default' }}>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{t.tag ?? '—'}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{namen(t.huidigeEigenaar) || <Box component="span" sx={{ color: '#b0b8c4', fontWeight: 400 }}>—</Box>}</TableCell>
                    <TableCell sx={{ color: t.werkend ? '#334155' : '#94a3b8', whiteSpace: 'nowrap' }}>{t.status || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.profiel || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.toegang || '—'}</TableCell>
                    <TableCell sx={{ color: '#94a3b8' }}>{namen(t.eerdereEigenaar)}</TableCell>
                    <TableCell sx={{ color: '#64748b', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.opmerkingen || ''}</TableCell>
                  </TableRow>
                ))}
                {zichtbaar.length === 0 && (
                  <TableRow><TableCell colSpan={7} sx={{ color: '#94a3b8', py: 4, textAlign: 'center' }}>{tags.length ? 'Geen tags in deze selectie.' : 'Nog geen tags gespiegeld uit Airtable.'}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <TagDialoog open={!!dialoog} tag={dialoog?.tag || null} opties={opties} personen={data?.personen || []}
        onClose={() => setDialoog(null)} onOpgeslagen={opgeslagen} />
    </Box>
  );
}
